import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';

export function buildMarketIntelligencePDFBlob(args) {
  return exportMarketIntelligencePDF({ ...args, returnBlob: true });
}

export function exportMarketIntelligencePDF({ tliResults, tliGroupedMarkets, tliAccountProfile, tliAnalyzedAt, companyName, returnBlob = false }) {
  const doc = new jsPDF({ orientation: 'portrait', unit: 'pt', format: 'letter' });

  const PAGE_W  = 612;
  const PAGE_H  = 792;
  const MARGIN  = 40;
  const CONTENT_W = PAGE_W - MARGIN * 2;

  let y = MARGIN;

  const checkPage = (needed = 20) => {
    if (y + needed > PAGE_H - MARGIN) {
      doc.addPage();
      y = MARGIN;
    }
  };

  const wrapText = (text, x, maxW, size, rgb, lineH) => {
    doc.setFontSize(size);
    doc.setTextColor(...rgb);
    const lines = doc.splitTextToSize(String(text || ''), maxW);
    lines.forEach(line => {
      checkPage(lineH);
      doc.text(line, x, y);
      y += lineH;
    });
  };

  // ── Header bar ──────────────────────────────────────────────────────────────
  doc.setFillColor(26, 35, 50);
  doc.rect(0, 0, PAGE_W, 58, 'F');

  doc.setFontSize(17);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(255, 255, 255);
  doc.text('MARKET INTELLIGENCE REPORT', MARGIN, 28);

  doc.setFontSize(9);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(180, 195, 210);
  doc.text(companyName || 'Unknown Account', MARGIN, 46);
  const genDate = new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
  doc.text(`Generated: ${genDate}`, PAGE_W - MARGIN, 46, { align: 'right' });

  y = 74;

  // ── Account Profile ─────────────────────────────────────────────────────────
  const prof = tliAccountProfile || {};
  const profileRows = [];

  if (prof.state)                             profileRows.push(['Domicile State',    prof.state]);
  if (prof.opStates?.length)                  profileRows.push(['Operating States',  prof.opStates.join(', ')]);
  if (prof.units != null)                     profileRows.push(['Power Units',       String(prof.units)]);
  if (prof.yib != null && prof.yib > 0)       profileRows.push(['Years in Business', String(prof.yib)]);
  if (prof.commsArr?.length)                  profileRows.push(['Commodities',       prof.commsArr.join(', ')]);
  if (prof.cabAlerts != null)                 profileRows.push(['FMCSA Alerts',      String(prof.cabAlerts)]);
  if (prof.telematicsPresent != null)         profileRows.push(['Telematics',        prof.telematicsPresent ? 'Yes' : 'No']);
  if (prof.hasB1Drivers != null)              profileRows.push(['B1/Intl Drivers',   prof.hasB1Drivers ? 'Yes' : 'No']);
  if (prof.naftaOperations != null)           profileRows.push(['NAFTA Operations',  prof.naftaOperations ? 'Yes' : 'No']);
  if (prof.radius != null)                    profileRows.push(['Operating Radius',  `${prof.radius} mi`]);
  if (prof.dotRating)                         profileRows.push(['DOT Safety Rating', prof.dotRating]);
  if (prof.maxVehicleAge != null)             profileRows.push(['Max Vehicle Age',   `${prof.maxVehicleAge} yrs`]);

  if (profileRows.length > 0) {
    checkPage(20);
    doc.setFontSize(9);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(71, 85, 105);
    doc.text('ACCOUNT PROFILE USED', MARGIN, y);
    y += 6;

    autoTable(doc, {
      startY: y,
      head:   [],
      body:   profileRows,
      margin: { left: MARGIN, right: MARGIN },
      styles: { fontSize: 8.5, cellPadding: 4, textColor: [55, 65, 81] },
      columnStyles: {
        0: { fontStyle: 'bold', cellWidth: 130, textColor: [71, 85, 105] },
        1: { cellWidth: CONTENT_W - 130 },
      },
      theme: 'plain',
      tableLineColor: [226, 232, 240],
      tableLineWidth: 0.5,
      alternateRowStyles: { fillColor: [248, 250, 252] },
    });
    y = doc.lastAutoTable.finalY + 18;
  }

  // ── Dedup (mirrors renderIntelligence) ──────────────────────────────────────
  const seen = new Set();
  const all = (tliResults || []).filter(entry => {
    const key = (entry.carrier?.name || entry.op?.carrierId || '').toLowerCase().trim();
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });

  const counts = { GREEN: 0, YELLOW: 0, RED: 0 };
  all.forEach(r => { if (counts[r.result] !== undefined) counts[r.result]++; });
  const total = counts.GREEN + counts.YELLOW + counts.RED;

  // ── Summary line ────────────────────────────────────────────────────────────
  checkPage(24);
  doc.setFontSize(9.5);
  doc.setFont('helvetica', 'bold');

  let sx = MARGIN;
  const summaryParts = [
    { text: `${counts.GREEN} submit`,  rgb: [21, 128, 61]  },
    { text: ' · ',                     rgb: [100, 116, 139] },
    { text: `${counts.YELLOW} review`, rgb: [146, 64, 14]  },
    { text: ' · ',                     rgb: [100, 116, 139] },
    { text: `${counts.RED} decline`,   rgb: [153, 27, 27]  },
    { text: ` · ${total} total`,       rgb: [100, 116, 139] },
  ];
  summaryParts.forEach(({ text, rgb }) => {
    doc.setTextColor(...rgb);
    doc.text(text, sx, y);
    sx += doc.getTextWidth(text);
  });
  y += 22;

  // ── Market sections ─────────────────────────────────────────────────────────
  const SECTIONS = [
    { result: 'GREEN',  label: 'SUBMIT — STRONG FIT',          hBg: [220, 252, 231], hText: [21, 128, 61],  dot: [34, 197, 94]   },
    { result: 'YELLOW', label: 'REVIEW / POSSIBLE QUOTE',      hBg: [254, 252, 232], hText: [146, 64, 14], dot: [245, 158, 11]  },
    { result: 'RED',    label: 'DO NOT SUBMIT — HARD DECLINE', hBg: [254, 242, 242], hText: [153, 27, 27],  dot: [239, 68, 68]   },
  ];

  SECTIONS.forEach(({ result, label, hBg, hText, dot }) => {
    const markets = all.filter(r => r.result === result);
    if (markets.length === 0) return;

    // Section header
    checkPage(36);
    doc.setFillColor(...hBg);
    doc.rect(MARGIN, y, CONTENT_W, 22, 'F');
    doc.setFillColor(...dot);
    doc.rect(MARGIN, y, 4, 22, 'F');
    doc.setFontSize(9.5);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(...hText);
    doc.text(`${label}  (${markets.length})`, MARGIN + 12, y + 14);
    y += 30;

    markets.forEach(({ op, carrier: car, channel: ch, result: _r, hardReasons, softFlags, fitReasons, missingData, decision, allChannelContacts }) => {
      const grouped       = (tliGroupedMarkets || []).find(g => g.id === op?.id);
      const cardPrograms  = grouped?.programs || [];

      // Carrier name + channel
      checkPage(36);
      doc.setFillColor(248, 250, 252);
      doc.rect(MARGIN, y, CONTENT_W, 24, 'F');
      doc.setFillColor(...dot);
      doc.rect(MARGIN, y, 3, 24, 'F');
      doc.setDrawColor(226, 232, 240);
      doc.setLineWidth(0.5);
      doc.rect(MARGIN, y, CONTENT_W, 24, 'S');

      const carrierLabel = car?.name || op?.carrierId || 'Unknown Carrier';
      doc.setFontSize(10.5);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(26, 35, 50);
      doc.text(carrierLabel, MARGIN + 10, y + 10);

      if (ch?.name) {
        doc.setFontSize(8.5);
        doc.setFont('helvetica', 'normal');
        doc.setTextColor(100, 116, 139);
        doc.text(`via ${ch.name}`, MARGIN + 10, y + 20);
      }
      y += 30;

      // Decision
      if (decision) {
        checkPage(14);
        doc.setFontSize(8.5);
        doc.setFont('helvetica', 'italic');
        doc.setTextColor(55, 65, 81);
        const dLines = doc.splitTextToSize(decision, CONTENT_W - 12);
        dLines.forEach(line => { checkPage(13); doc.text(line, MARGIN + 6, y); y += 13; });
        y += 4;
      }

      // Bullet list helper
      const bullets = (header, items, rgb) => {
        if (!items?.length) return;
        checkPage(16);
        doc.setFontSize(8);
        doc.setFont('helvetica', 'bold');
        doc.setTextColor(...rgb);
        doc.text(header, MARGIN + 6, y);
        y += 12;
        items.forEach(item => {
          const iLines = doc.splitTextToSize(`• ${item}`, CONTENT_W - 22);
          iLines.forEach((line, i) => {
            checkPage(12);
            doc.setFont('helvetica', 'normal');
            doc.setTextColor(55, 65, 81);
            doc.setFontSize(8);
            doc.text(line, MARGIN + (i === 0 ? 12 : 18), y);
            y += 12;
          });
        });
        y += 4;
      };

      bullets('Hard Declines',       hardReasons,                  [153, 27, 27]  );
      bullets('Needs Review',        softFlags,                    [146, 64, 14] );
      bullets('Missing Account Data',missingData,                  [100, 116, 139]);
      if (result === 'GREEN') bullets('Rules Passed', (fitReasons || []).slice(0, 8), [21, 128, 61]);

      // Submission instructions
      if (op?.submissionInstructions) {
        checkPage(16);
        doc.setFontSize(8);
        doc.setFont('helvetica', 'bold');
        doc.setTextColor(100, 116, 139);
        doc.text('Submission Instructions', MARGIN + 6, y);
        y += 12;
        const iLines = doc.splitTextToSize(op.submissionInstructions, CONTENT_W - 22);
        iLines.forEach(line => { checkPage(12); doc.setFont('helvetica', 'normal'); doc.setFontSize(8); doc.setTextColor(55, 65, 81); doc.text(line, MARGIN + 12, y); y += 12; });
        y += 4;
      }

      // Contacts
      const contacts = allChannelContacts?.length ? allChannelContacts : (ch?.contactName ? [ch] : []);
      if (contacts.length > 0) {
        checkPage(16);
        doc.setFontSize(8);
        doc.setFont('helvetica', 'bold');
        doc.setTextColor(100, 116, 139);
        doc.text('Contact', MARGIN + 6, y);
        y += 12;
        contacts.forEach(ct => {
          const parts = [ct.contactName || ct.name, ct.contactEmail, ct.contactPhone].filter(Boolean).join('  |  ');
          if (!parts) return;
          const cLines = doc.splitTextToSize(parts, CONTENT_W - 22);
          cLines.forEach(line => { checkPage(12); doc.setFont('helvetica', 'normal'); doc.setFontSize(8); doc.setTextColor(55, 65, 81); doc.text(line, MARGIN + 12, y); y += 12; });
        });
        y += 4;
      }

      // Programs
      if (cardPrograms.length > 0) {
        checkPage(16);
        doc.setFontSize(8);
        doc.setFont('helvetica', 'bold');
        doc.setTextColor(100, 116, 139);
        doc.text('Available Programs', MARGIN + 6, y);
        y += 12;
        cardPrograms.forEach(p => {
          const statusRgb = p.status === 'GREEN' ? [21, 128, 61] : p.status === 'YELLOW' ? [146, 64, 14] : [153, 27, 27];
          checkPage(14);
          doc.setFont('helvetica', 'bold');
          doc.setFontSize(8);
          doc.setTextColor(...statusRgb);
          doc.text(`• ${p.name || 'Program'}`, MARGIN + 12, y);
          y += 12;
          if (p.decision) {
            const pLines = doc.splitTextToSize(p.decision, CONTENT_W - 30);
            pLines.forEach(line => { checkPage(12); doc.setFont('helvetica', 'normal'); doc.setFontSize(8); doc.setTextColor(100, 116, 139); doc.text(line, MARGIN + 18, y); y += 12; });
          }
        });
        y += 4;
      }

      // Divider
      checkPage(14);
      doc.setDrawColor(226, 232, 240);
      doc.setLineWidth(0.5);
      doc.line(MARGIN, y, MARGIN + CONTENT_W, y);
      y += 12;
    });

    y += 6;
  });

  // ── Footer ──────────────────────────────────────────────────────────────────
  if (tliAnalyzedAt) {
    checkPage(18);
    doc.setFontSize(7.5);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(148, 163, 184);
    const evalStr = new Date(tliAnalyzedAt).toLocaleString();
    doc.text(`Last evaluated: ${evalStr}`, MARGIN, y);
    doc.text(`${counts.GREEN} submit · ${counts.YELLOW} review · ${counts.RED} decline · ${total} total`, PAGE_W - MARGIN, y, { align: 'right' });
  }

  // ── Output ──────────────────────────────────────────────────────────────────
  if (returnBlob) {
    return doc.output('blob');
  }
  const safeName = (companyName || 'Account')
    .replace(/[^a-zA-Z0-9\s\-_]/g, '')
    .trim()
    .replace(/\s+/g, '-') || 'Account';
  doc.save(`${safeName}-Market-Intelligence.pdf`);
}
